export const mediaQueries = {
	mobileWidth: '@media (max-width: 600px)',
	tabletWidth: '@media (min-width: 601px) and (max-width: 1024px)',
	/* mediaWidth/desktopWidth must stay exhaustive and meet exactly at the
	   1080px smallDesktop boundary: a gap between them leaves QuizContainer
	   (and every other paired user) with NEITHER branch applied, which drops
	   the top-nav clearance and mixes mobile and desktop styling. */
	mediaWidth: '@media (max-width: 1079px)',
	desktopWidth: '@media (min-width: 1080px)',
};

// Legacy color-name enum. Kept as a compatibility shim while pages are
// migrated to the redesign tokens (theme.design); remove once unused.
export enum Colors {
	surfaceMain = 'surfaceMain',
	surfaceMinimal = 'surfaceMinimal',
	surfaceLight = 'surfaceLight',
	surfaceSoft = 'surfaceSoft',
	surfaceMuted = 'surfaceMuted',
	surfaceNeutral = 'surfaceNeutral',
	surfaceBold = 'surfaceBold',
	surfaceBrave = 'surfaceBrave',

	transparentIndicator = 'transparentIndicator',
	transparentBrand = 'transparentBrand',
	transparentSoft = 'transparentSoft',
	transparent = 'transparent',

	quizBackground = 'quizBackground',

	textMain = 'textMain',
	textSoft = 'textSoft',
	textMinimal = 'textMinimal',
	textMuted = 'textMuted',
	textBrand = 'textBrand',
	textReverse = 'textReverse',
	textStrong = 'textStrong',

	brandFeather = 'brandFeather',
	brandTempera = 'brandTempera',
	brandSoft = 'brandSoft',
	brandMain = 'brandMain',
	brandGrumpy = 'brandGrumpy',
	brandPale = 'brandPale',
	brandDark = 'brandDark',
	brandMidnight = 'brandMidnight',

	pointsBackground = 'pointsBackground',
	pointsText = 'pointsText',
	questionContainer = 'questionContainer',
	answerContainer = 'answerContainer',
	indicator = 'indicator',
	qupitnik = 'qupitnik',

	black = 'black',
	white = 'white',
	none = 'none',

	warning = 'warning',
	lightwarning = 'lightwarning',
}

// ------------------------------------------------------------------ //
// Quizzach redesign design system.
// Source of truth: investigation-todos/quizzach-redesign-mockup-FINAL-2026-07-08.html
// ------------------------------------------------------------------ //

export interface DesignTokens {
	/** page background (cream in light, espresso in dark) */
	bg: string;
	/** raised panel surface */
	panel: string;
	/** brighter panel / input surface */
	panel2: string;
	/** ruled-paper block background */
	paper: string;
	/** strongest text */
	ink: string;
	/** ink as "r,g,b" for alpha composition */
	inkRgb: string;
	/** secondary text */
	brown: string;
	/** muted text, never lighter than this */
	muted: string;
	/** handwritten notes on flat background */
	goldInk: string;
	/** hairline dividers (ink at .12 alpha) */
	hair: string;
	/** brand teal: buttons, active states, progress, play */
	teal: string;
	/** teal hover */
	tealSoft: string;
	/** text on teal fills */
	tealDeep: string;
	/** active/link teal that holds contrast on the page background */
	tealDark: string;
	/** accent words, prices */
	coral: string;
	/** gold accents (only on dark surfaces in light mode) */
	gold: string;
	/** darkest stage/footer surface */
	black: string;
	/** logo color on plain page background */
	logo: string;
	/** cream tone for text over photos and dark media */
	creamOnDark: string;
}

const designLight: DesignTokens = {
	bg: '#F8F0E3',
	panel: '#ECE4D7',
	panel2: '#FFF9EE',
	paper: '#ECE4D7',
	ink: '#1B140E',
	inkRgb: '27,20,14',
	brown: '#473E31',
	muted: '#6E6353',
	goldInk: '#A87C1F',
	hair: 'rgba(27,20,14,0.12)',
	teal: '#00FFC2',
	tealSoft: '#7CFFDF',
	tealDeep: '#0F3E32',
	tealDark: '#008061',
	coral: '#F16B46',
	gold: '#DBB154',
	black: '#180E02',
	logo: '#00B388',
	creamOnDark: '#FBF5EA',
};

const designDark: DesignTokens = {
	bg: '#191B1D',
	panel: '#1D1F21',
	panel2: '#26282B',
	paper: '#2B2E31',
	ink: '#F7F7F7',
	inkRgb: '247,247,247',
	brown: '#C9CBCC',
	muted: '#9A9DA0',
	goldInk: '#DBB154',
	hair: 'rgba(247,247,247,0.12)',
	teal: '#00FFC2',
	tealSoft: '#7CFFDF',
	tealDeep: '#0F3E32',
	tealDark: '#00E6AF',
	coral: '#FF7E57',
	gold: '#DBB154',
	black: '#151617',
	logo: '#00FFC2',
	creamOnDark: '#FBF5EA',
};

const layout = {
	pad: 'max(clamp(20px, 5vw, 72px), calc((100vw - 1440px) / 2))',
	gut: 'clamp(10px, 1.4vw, 18px)',
	radius: '2rem',
};

const typography = {
	fontPrimary: '"Fjalla One", sans-serif',
	fontSecondary: '"Ubuntu Sans", sans-serif',
	// Rosario is dropped from the redesign; legacy fontNum consumers get body font.
	fontNum: '"Ubuntu Sans", sans-serif',
	fontHandwritten: '"Caveat", cursive',
};

// for_kids quizzes: the play stage swaps every face to DynaPuff — layout,
// colors and sizes stay the same, only the voice softens.
export const kidsPlayTypography = {
	fontPrimary: '"DynaPuff", system-ui',
	fontSecondary: '"DynaPuff", system-ui',
	fontNum: '"DynaPuff", system-ui',
	fontHandwritten: '"DynaPuff", cursive',
};

// ------------------------------------------------------------------ //
// Compatibility palette. Maps the legacy palette shape (consumed via
// Colors/handleColorType and theme.palette across not-yet-redesigned
// components) onto the redesign tokens, so the whole app flips to the
// new palettes immediately. Redesigned components should use
// theme.design instead; this layer shrinks page by page.
// ------------------------------------------------------------------ //

const buildCompatPalette = (d: DesignTokens, mode: 'light' | 'dark') => {
	const frost =
		mode === 'light' ? 'rgba(248,240,227,0.78)' : 'rgba(25,27,29,0.72)';
	const surface =
		mode === 'light'
			? {
					main: d.bg,
					minimal: d.panel,
					light: '#D7CFC3',
					soft: '#B6AEA3',
					muted: '#958D83',
					neutral: d.muted,
					bold: d.brown,
					brave: d.ink,
					transparent: frost,
					transparentBrand: 'rgba(0,255,194,0.4)',
					transparentIndicator: d.hair,
					quizBackground: d.bg,
				}
			: {
					main: d.black,
					minimal: d.bg,
					light: d.panel,
					soft: d.panel2,
					muted: '#33363A',
					neutral: '#4E5257',
					bold: d.muted,
					brave: d.brown,
					transparent: frost,
					transparentBrand: 'rgba(0,255,194,0.4)',
					transparentIndicator: d.hair,
					quizBackground: d.bg,
				};
	const text =
		mode === 'light'
			? {
					main: d.ink,
					soft: d.brown,
					minimal: d.brown,
					muted: d.muted,
					brand: d.tealDark,
					reverse: d.bg,
					strong: d.ink,
				}
			: {
					main: d.ink,
					soft: d.brown,
					minimal: d.brown,
					muted: d.muted,
					brand: d.tealDark,
					reverse: d.bg,
					strong: d.teal,
				};
	const brand = {
		feather: d.tealSoft,
		tempera: d.tealSoft,
		soft: d.tealSoft,
		main: d.teal,
		grumpy: d.tealSoft,
		pale: d.logo,
		dark: d.tealDark,
		midnight: 'rgba(15,62,50,0.57)',
		night: d.tealDeep,
	};
	const transparent = {
		soft: mode === 'light' ? 'rgba(236,228,215,0.7)' : 'rgba(38,40,43,0.7)',
		light: mode === 'light' ? 'rgba(27,20,14,0.08)' : 'rgba(247,247,247,0.08)',
		strong: 'rgba(24,14,2,0.45)',
		bold: 'rgba(24,14,2,0.62)',
		brave: 'rgba(24,14,2,0.45)',
		brand: 'rgba(0,255,194,0.4)',
		// frosted backdrop for play chrome (bottom bars, nav buttons)
		deep: mode === 'light' ? 'rgba(248,240,227,0.94)' : 'rgba(21,22,23,0.94)',
	};

	return {
		black: '#000000',
		white: '#ffffff',
		none: '',
		brand,
		transparent,
		surface,
		text,
		button: {
			text: d.tealDeep,
			backgroundMain: d.teal,
			backgroundTransparent: surface.transparent,
			backgroundHover: d.tealSoft,
			border: d.teal,
			borderFocus: d.ink,
		},
		circle: {
			number: d.tealDark,
			numberClicked: d.bg,
			background: mode === 'light' ? d.ink : d.panel2,
			backgroundClicked: d.tealDark,
		},
		navigation: {
			background: surface.transparent,
			text: text.main,
		},
		premade: {
			text: d.creamOnDark,
			reverse: d.ink,
			transparent: 'rgba(24,14,2,0.45)',
		},
		quiz: {
			navigationBackground: d.bg,
			mainBackground: d.bg,
			pointsBackground: mode === 'light' ? d.ink : d.teal,
			pointsText: mode === 'light' ? d.teal : d.tealDeep,
			questionContainer: frost,
			answerContainer:
				mode === 'light' ? 'rgba(0,255,194,0.28)' : 'rgba(0,255,194,0.12)',
			indicator: d.muted,
			qupitnik: d.muted,
			indicatorSoft: mode === 'light' ? d.panel : d.panel2,
			surfaceMain: d.bg,
			surfaceSoft: d.panel,
			surfaceTransparent: frost,
			surfaceBrave: mode === 'light' ? d.ink : d.brown,
			surfaceMuted: d.muted,
			textMain: text.main,
			textSoft: text.soft,
			textMuted: text.muted,
			textBrand: text.brand,
			textReverse: text.reverse,
			textStrong: text.strong,
			brandMain: d.teal,
			brandPale: d.logo,
			brandDark: d.tealDark,
		},
		feedback: {
			title: d.panel,
			subtitle: d.muted,
			option: d.panel2,
		},
		warning: '#B10B0B',
		lightwarning: '#FFCDCD',
	};
};

export const themeLight = {
	name: 'light',
	mode: 'light' as const,
	design: designLight,
	layout,
	typography: { ...typography },
	palette: buildCompatPalette(designLight, 'light'),
};

export const themeDark = {
	name: 'dark',
	mode: 'dark' as const,
	design: designDark,
	layout,
	typography: { ...typography },
	palette: buildCompatPalette(designDark, 'dark'),
};

export const breakpoints = {
	smallMobile: 320,
	mobile: 481,
	tablet: 601,
	largeTablet: 769,
	smallDesktop: 1080,
	largeDesktop: 1281,
	extraLargeDesktop: 1611,
	ultrawide: 1801,
};

export const media = {
	mobile: '@media (min-width: 481px)',
	tablet: '@media (min-width: 601px)',
	largeTablet: '@media (min-width: 769px)',
	smallDesktop: '@media (min-width: 1080px)',
	largeDesktop: '@media (min-width: 1281px)',
	extraLargeDesktop: '@media (min-width: 1611px)',
	ultrawide: '@media (min-width: 1801px)',
};

export const handleColorType = (color: string, theme: any) => {
	switch (color) {
		// surface
		case Colors.surfaceMain:
			return theme.palette.surface.main;
		case Colors.surfaceMuted:
			return theme.palette.surface.muted;
		case Colors.surfaceMinimal:
			return theme.palette.surface.minimal;
		case Colors.surfaceLight:
			return theme.palette.surface.light;
		case Colors.surfaceSoft:
			return theme.palette.surface.soft;
		case Colors.surfaceNeutral:
			return theme.palette.surface.neutral;
		case Colors.surfaceBold:
			return theme.palette.surface.bold;
		case Colors.surfaceBrave:
			return theme.palette.surface.brave;

		case Colors.quizBackground:
			return theme.palette.quiz.mainBackground;

		// transparent
		case Colors.transparent:
			return theme.palette.surface.transparent;
		case Colors.transparentBrand:
			return theme.palette.surface.transparentBrand;
		case Colors.transparentIndicator:
			return theme.palette.surface.transparentIndicator;
		case Colors.transparentSoft:
			return theme.palette.transparent.soft;

		// text

		case Colors.textMain:
			return theme.palette.text.main;
		case Colors.textSoft:
			return theme.palette.text.soft;
		case Colors.textMinimal:
			return theme.palette.text.minimal;
		case Colors.textMuted:
			return theme.palette.text.muted;
		case Colors.textBrand:
			return theme.palette.text.brand;
		case Colors.textReverse:
			return theme.palette.text.reverse;
		case Colors.textStrong:
			return theme.palette.text.strong;

		case Colors.pointsBackground:
			return theme.palette.quiz.pointsBackground;
		case Colors.pointsText:
			return theme.palette.quiz.pointsText;
		case Colors.questionContainer:
			return theme.palette.quiz.questionContainer;
		case Colors.answerContainer:
			return theme.palette.quiz.answerContainer;
		case Colors.indicator:
			return theme.palette.quiz.indicator;

		case Colors.qupitnik:
			return theme.palette.quiz.qupitnik;

		// brand
		case Colors.brandFeather:
			return theme.palette.brand.soft;
		case Colors.brandTempera:
			return theme.palette.brand.tempera;
		case Colors.brandSoft:
			return theme.palette.brand.soft;
		case Colors.brandMain:
			return theme.palette.brand.main;
		case Colors.brandGrumpy:
			return theme.palette.brand.grumpy;
		case Colors.brandPale:
			return theme.palette.brand.pale;
		case Colors.brandDark:
			return theme.palette.brand.dark;
		case Colors.brandMidnight:
			return theme.palette.brand.midnight;

		// bNw
		case Colors.black:
			return theme.palette.black;
		case Colors.white:
			return theme.palette.white;
		case Colors.none:
			return theme.palette.none;
		case Colors.lightwarning:
			return theme.palette.lightwarning;
		case Colors.warning:
			return theme.palette.warning;
		default:
			return '#ffffff';
	}
};

export const ResponsiveTextSize = {
	mobile: {
		xxs: '8px',
		xs: '10px',
		s: '14px',
		m: '16px',
		l: '20px',
		xl: '30px',
		xxl: '40px',
		xxxl: '50px',
	},
	tablet: {
		xxs: '10px',
		xs: '12px',
		s: '14px',
		m: '16px',
		l: '22px',
		xl: '32px',
		xxl: '42px',
		xxxl: '52px',
	},
	largeTablet: {
		xxs: '10px',
		xs: '12px',
		s: '14px',
		m: '16px',
		l: '22px',
		xl: '32px',
		xxl: '42px',
		xxxl: '52px',
	},
	smallDesktop: {
		xxs: '10px',
		xs: '12px',
		s: '14px',
		m: '16px',
		l: '22px',
		xl: '32px',
		xxl: '40px',
		xxxl: '50px',
	},
	largeDesktop: {
		xxs: '10px',
		xs: '12px',
		s: '16px',
		m: '18px',
		l: '22px',
		xl: '34px',
		xxl: '44px',
		xxxl: '54px',
	},
	extraLargeDesktop: {
		xxs: '10px',
		xs: '12px',
		s: '16px',
		m: '18px',
		l: '24px',
		xl: '34px',
		xxl: '44px',
		xxxl: '54px',
	},
	ultrawide: {
		xxs: '12px',
		xs: '14px',
		s: '20px',
		m: '22px',
		l: '26px',
		xl: '36px',
		xxl: '44px',
		xxxl: '54px',
	},
};

export enum TextSize {
	xxs = 'xxs',
	xs = 'xs',
	s = 's',
	m = 'm',
	l = 'l',
}
